<script lang="ts" setup>
/* eslint vue/no-v-html: "off" */
import { createHighlighter, type BundledHighlighterOptions, type BuiltinLanguage, type BuiltinTheme } from "shiki";
import nord from "shiki/themes/nord.mjs";
import { useClipboard } from "#imports";
import { faCheck, faCopy } from "@fortawesome/pro-light-svg-icons";

export interface UICodeProps {
    name?: string;
    code?: any;
    width?: "fit" | "auto";
    lang: BuiltinLanguage;
    theme?: BuiltinTheme;
    fallback?: string;
}

const props = withDefaults(defineProps<UICodeProps>(), {
    theme: "nord",
    width: "fit",
    fallback: "Empty value passed to code block",
});

const text = computed(() => {
    if (!props.code) return props.fallback;

    let code = props.code;

    if (["object", "function"].includes(typeof code) || Array.isArray(code)) {
        const cloned = useCloneDeep(code);

        if (Object.keys(cloned).length === 0) {
            code = props.fallback;
        } else {
            code = JSON.stringify(cloned, null, 2);
        }
    }
    else code = code.toString();

    return code;
});

const highlighter = await createHighlighter({
    themes: [nord],
    langs: [props.lang],
} as BundledHighlighterOptions<BuiltinLanguage, BuiltinTheme>);

const content = computed(() => {
    return highlighter.codeToHtml(text.value, {
        theme: props.theme,
        lang: props.lang as BuiltinLanguage,
    });
});

const { copy, copied } = useClipboard();
</script>

<template>
    <div class="relative overflow-hidden group" :class="width === 'fit' ? 'max-w-max' : ''">
        <div v-if="content"
            class="[&>.shiki]:p-4 overflow-auto [&>.shiki]:w-fit [&>.shiki>code]:flex [&>.shiki>code]:flex-col" :class="{
                '[&>.shiki]:min-w-fit': width === 'fit',
                '[&>.shiki]:min-w-full': width === 'auto',
            }" v-html="content" />
        <UButton variant="ghost"
            class="absolute z-10 top-2.5 right-2.5 opacity-0 group-hover:opacity-100 hover:bg-transparent!" square
            color="white" size="xl" @click="copy(text)">
            <FontAwesomeIcon class="text-white" :icon="copied ? faCheck : faCopy" />
        </UButton>
        <div class="absolute bottom-0 right-0 text-white p-4">{{ name }}</div>
    </div>
</template>
